import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Group,
  Loader,
  MultiSelect,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCalendarEvent,
  IconCar,
  IconClipboardCheck,
  IconFileInvoice,
  IconReportMoney,
} from '@tabler/icons-react';
import { getAccentColor } from '../../lib/jlfcTheme';
import { authFetch, parseJsonResponse } from '../../lib/api';
import { formatMoney, formatNumber } from '../../lib/statistiquesFormat';
import MonthlyLineChart from '../statistiques/MonthlyLineChart';
import YearTotalsPanel from '../statistiques/YearTotalsPanel';
import SearchFilterInput from '../SearchFilterInput';
import TableActionIcon from '../TableActionIcon';

const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_YEARS = [String(CURRENT_YEAR - 1), String(CURRENT_YEAR)];

function sortYearsAsc(years) {
  return [...years].sort((a, b) => Number(a) - Number(b));
}

const METRIC_OPTIONS = [
  { value: 'all', label: 'Tous les indicateurs' },
  { value: 'controles', label: 'Contrôles' },
  { value: 'factures', label: 'Factures (CA TTC)' },
  { value: 'reglements', label: 'Règlements' },
];

function formatMoneyFull(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
}

function StatCard({ icon: Icon, label, value, hint, color }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon size="lg" radius="md" color={color || getAccentColor()} variant="light">
          <Icon size={20} />
        </ThemeIcon>
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            {label}
          </Text>
          <Text size="xl" fw={700} lh={1.2}>
            {value}
          </Text>
          {hint ? (
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          ) : null}
        </Stack>
      </Group>
    </Paper>
  );
}

function buildChartSeries(evolution, key) {
  return (evolution?.series || []).map((entry) => ({
    year: entry.year,
    data: entry[key] || [],
  }));
}

export default function ClientPortalStatsPanel({ refreshKey = 0 }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedYears, setSelectedYears] = useState(DEFAULT_YEARS);
  const [metric, setMetric] = useState('all');
  const [yearSearch, setYearSearch] = useState('');

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        years: selectedYears.join(','),
      });
      const res = await authFetch(`/api/client-portal/stats?${params.toString()}`);
      const data = await parseJsonResponse(res);
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Chargement impossible');
      }
      setStats(data.data || null);
    } catch (err) {
      setError(err.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [selectedYears]);

  useEffect(() => {
    loadStats();
  }, [loadStats, refreshKey]);

  const yearOptions = useMemo(() => {
    const fromApi = (stats?.availableYears || []).map((y) => String(y));
    const merged = new Set([...fromApi, ...selectedYears, ...DEFAULT_YEARS]);
    return [...merged]
      .sort((a, b) => Number(b) - Number(a))
      .map((value) => ({ value, label: value }));
  }, [stats?.availableYears, selectedYears]);

  const filteredYearOptions = useMemo(() => {
    const q = yearSearch.trim().toLowerCase();
    if (!q) return yearOptions;
    return yearOptions.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [yearOptions, yearSearch]);

  const filteredYearTotals = useMemo(() => {
    const totals = stats?.yearTotals || [];
    const q = yearSearch.trim();
    if (!q) return totals;
    return totals.filter((row) => String(row.year).includes(q));
  }, [stats?.yearTotals, yearSearch]);

  const controlesSeries = useMemo(
    () => buildChartSeries(stats?.evolution, 'controles'),
    [stats?.evolution],
  );
  const facturesSeries = useMemo(
    () => buildChartSeries(stats?.evolution, 'facturesTtc'),
    [stats?.evolution],
  );
  const reglementsSeries = useMemo(
    () => buildChartSeries(stats?.evolution, 'reglements'),
    [stats?.evolution],
  );

  const shiftYears = (delta) => {
    setSelectedYears((prev) => {
      const next = prev.map((y) => String(Number(y) + delta));
      return next.length ? sortYearsAsc(next) : DEFAULT_YEARS;
    });
  };

  const periodLabel =
    selectedYears.length > 1
      ? `${Math.min(...selectedYears.map(Number))}–${Math.max(...selectedYears.map(Number))}`
      : selectedYears[0] || '';

  const showControles = metric === 'all' || metric === 'controles';
  const showFactures = metric === 'all' || metric === 'factures';
  const showReglements = metric === 'all' || metric === 'reglements';

  if (loading && !stats) {
    return (
      <Group justify="center" py="xl">
        <Loader type="dots" />
      </Group>
    );
  }

  if (error && !stats) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" title="Erreur">
        {error}
      </Alert>
    );
  }

  if (!stats) return null;

  const period = stats.periodSummary || {};

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Title order={4}>Synthèse</Title>
        {loading ? <Loader size="sm" color={getAccentColor()} /> : null}
      </Group>

      <Paper withBorder p="md" radius="md">
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          <Stack gap={6}>
            <Text size="sm" fw={500}>
              Années à comparer
            </Text>
            <Group gap="xs" wrap="nowrap">
              <TableActionIcon
                label="Reculer d'une année"
                variant="default"
                size="lg"
                onClick={() => shiftYears(-1)}
              >
                &lt;
              </TableActionIcon>
              <MultiSelect
                flex={1}
                placeholder="Choisir les années"
                data={filteredYearOptions}
                value={selectedYears}
                onChange={(values) => setSelectedYears(sortYearsAsc(values))}
                searchable
                clearable
                maxDropdownHeight={220}
              />
              <TableActionIcon
                label="Avancer d'une année"
                variant="default"
                size="lg"
                onClick={() => shiftYears(1)}
              >
                &gt;
              </TableActionIcon>
            </Group>
          </Stack>

          <Select
            label="Indicateur affiché"
            data={METRIC_OPTIONS}
            value={metric}
            onChange={(v) => v && setMetric(v)}
          />

          <Stack gap={6}>
            <Text size="sm" fw={500}>
              Rechercher une année
            </Text>
            <SearchFilterInput
              placeholder="Filtrer les totaux annuels…"
              value={yearSearch}
              onChange={setYearSearch}
              name="jlfc-client-stats-year-search"
              aria-label="Filtrer par année"
            />
          </Stack>
        </SimpleGrid>
      </Paper>

      {error ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Erreur">
          {error}
        </Alert>
      ) : null}

      <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="md">
        <StatCard
          icon={IconClipboardCheck}
          label="Contrôles"
          value={stats.controles?.total ?? 0}
          hint={
            selectedYears.length
              ? `${period.controles ?? 0} sur ${periodLabel}`
              : stats.controles?.expiring_soon > 0
                ? `${stats.controles.expiring_soon} expire${stats.controles.expiring_soon > 1 ? 'nt' : ''} sous 30 jours`
                : stats.controles?.relances > 0
                  ? `${stats.controles.relances} relance${stats.controles.relances > 1 ? 's' : ''}`
                  : null
          }
          color="teal"
        />
        <StatCard
          icon={IconFileInvoice}
          label="Factures"
          value={stats.factures?.total ?? 0}
          hint={
            selectedYears.length
              ? `${period.factures ?? 0} sur ${periodLabel} · CA ${formatMoney(period.ca_ttc)}`
              : `${stats.factures?.soldees ?? 0} soldée${(stats.factures?.soldees ?? 0) > 1 ? 's' : ''}`
          }
          color="blue"
        />
        <StatCard
          icon={IconReportMoney}
          label="Restant dû"
          value={formatMoneyFull(stats.factures?.restant_du)}
          hint={`Total TTC : ${formatMoneyFull(stats.factures?.montant_ttc)}`}
          color="orange"
        />
        <StatCard
          icon={IconCar}
          label="Engins"
          value={stats.engins?.total ?? 0}
          color="cyan"
        />
        <StatCard
          icon={IconReportMoney}
          label="Règlements"
          value={formatMoneyFull(stats.reglements?.montant)}
          hint={
            selectedYears.length
              ? `${formatMoneyFull(period.reglements)} sur ${periodLabel}`
              : `${stats.reglements?.total ?? 0} opération${(stats.reglements?.total ?? 0) > 1 ? 's' : ''}`
          }
          color="green"
        />
        <StatCard
          icon={IconCalendarEvent}
          label="RDV"
          value={
            <Group gap="xs">
              <span>{stats.rdv?.accepted ?? 0} accepté{(stats.rdv?.accepted ?? 0) > 1 ? 's' : ''}</span>
              {(stats.rdv?.pending ?? 0) > 0 ? (
                <Badge color="orange" variant="light" size="sm">
                  {stats.rdv.pending} en attente
                </Badge>
              ) : null}
            </Group>
          }
          color="violet"
        />
      </SimpleGrid>

      {filteredYearTotals.length > 0 ? (
        <YearTotalsPanel
          totals={filteredYearTotals}
          title="Comparatif annuel (variation vs année précédente)"
        />
      ) : yearSearch ? (
        <Text size="sm" c="dimmed">
          Aucune année ne correspond à « {yearSearch} ».
        </Text>
      ) : null}

      {selectedYears.length > 0 ? (
        <Stack gap="lg">
          <Text size="sm" fw={600}>
            Évolution mensuelle
          </Text>
          {showControles ? (
            <MonthlyLineChart
              title="Contrôles par mois"
              subtitle={`${selectedYears.length} année(s) comparée(s)`}
              series={controlesSeries}
              valueKey="count"
              formatValue={formatNumber}
            />
          ) : null}
          {showFactures ? (
            <MonthlyLineChart
              title="Chiffre d'affaires facturé (TTC)"
              subtitle={`${selectedYears.length} année(s) comparée(s)`}
              series={facturesSeries}
              valueKey="amount"
              formatValue={formatMoney}
            />
          ) : null}
          {showReglements ? (
            <MonthlyLineChart
              title="Encaissements par mois"
              subtitle={`${selectedYears.length} année(s) comparée(s)`}
              series={reglementsSeries}
              valueKey="amount"
              formatValue={formatMoney}
            />
          ) : null}
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">
          Sélectionnez au moins une année pour afficher les graphiques.
        </Text>
      )}
    </Stack>
  );
}
